import "../../../components/graphic-toggle";
import "./walk-in-panel";
import GraphicToggle from "../../../components/graphic-toggle";
import WalkInPanel from "./walk-in-panel";
import elt from "../../../html/elt";
import getLogger from "../../../logging";
import injectCSS from "../../../html/inject-css";
import { SavedViewConfig, savedViewDefaults } from "../../../views/views-config";
import { clone } from "../../../json/optional-properties";
import { registerListener } from "../../../html/register-listener";
import { uuid4 } from "../../../utils";

// TODO:
//  - prevent saving view with blank name

const log = getLogger('walk-in');

export class WalkInViewsPanel extends WalkInPanel {

    views: SavedViewConfig[];
    current: SavedViewConfig;

    editBtn: HTMLButtonElement =
        elt('button',
            {
                class: 'btn btn-secondary',
                title: 'Edit the list of views',
            },
            elt('i', {class: 'bi bi-list-check'}),
            'Edit');
    updateCurrentBtn: HTMLButtonElement = 
        elt('button',
            {
                class: 'btn btn-secondary',
                title: 'Update the selected view to match the current 3D scene',
            },
            elt('i', {class: 'bi bi-file-earmark-check'}),
            'Update');

    createViewNameInput: HTMLInputElement =
        elt('input', { class: 'form-control', placeholder: 'New View Name', });
    createViewBtn: HTMLButtonElement =
        elt('button', {class: 'btn btn-secondary'},
            elt('i', {class: 'bi-file-earmark-plus'}));
    createViewGroup: HTMLElement =
        elt('div', {class: 'create-view-group input-group'},
            this.createViewNameInput,
            this.createViewBtn);

    normalModeGrid: HTMLElement = elt('div', {class: 'normal-mode-grid'});
    normalMode: HTMLElement = elt('div', {},
                                  this.normalModeGrid,
                                  this.createViewGroup,
                                  this.editBtn,
                                  ' ',
                                  this.updateCurrentBtn,
                                 );

    cancelBtn: HTMLButtonElement = elt('button', {class: 'btn btn-secondary'}, 'Cancel');
    updateListBtn:  HTMLButtonElement =
        elt('button', {class: 'btn btn-secondary'},
            elt('i', {class: 'bi bi-file-earmark-check'}),
            'Update');

    editModeGrid: HTMLElement = elt('div', {class: 'edit-mode-grid'});
    editMode: HTMLElement =
        elt('div', { class: 'edit-mode hidden' },
            this.editModeGrid,
            this.cancelBtn,
            ' ',
            this.updateListBtn);

    constructor(views: SavedViewConfig[]) {
        super();
        injectCSS(STYLESHEET);

        this.views = views;
        if(views.length < 1) {
            log.error('WalkInViewsPanel constructed with an empty views list. Adding default.');
            this.views.push(clone(savedViewDefaults));
        }
        this.current = views[0];

        registerListener(this.editBtn, 'click', () => {
            this.editMode.classList.remove('hidden');
            this.normalMode.classList.add('hidden');
            this.refresh();
        });

        registerListener(this.cancelBtn, 'click', () => {
            this.editMode.classList.add('hidden');
            this.normalMode.classList.remove('hidden');
            this.refresh();
        });

        registerListener(this.updateListBtn, 'click', () => {
            this.updateList();
            this.editMode.classList.add('hidden');
            this.normalMode.classList.remove('hidden');
            this.refresh();
        });

        registerListener(this.createViewNameInput, 'keypress', (event: KeyboardEvent) => {
            if(event.key == 'Enter') {
                this.createView();
            }
        });
        registerListener(this.createViewBtn, 'click', this.createView);
        registerListener(this.updateCurrentBtn, 'click', this.updateCurrentView);
    }

    connectedCallback() {
        this.append(this.normalMode);
        this.append(this.editMode);
    }

    show(under: HTMLElement) {
        super.show(under);
        this.refresh();
    }

    refresh = () => {
        // Empty the grids and rebuild
        this.normalModeGrid.replaceChildren();
        this.editModeGrid.replaceChildren();

        for(const view of this.views) {
            // First, normal mode
            if(!view.hidden) {
                const
                    id = uuid4(),
                    checked = view == this.current,
                    radio: HTMLInputElement = elt('input',
                        {
                            id,
                            type: 'radio',
                            name: 'walk-in-saved-view-select',
                            class: 'form-check-input',
                        });
                radio.checked = checked;
                registerListener(radio, 'change', () => {
                    this.current = view;
                    this.dispatchEvent(
                        new CustomEvent('apply-current-view-config', {bubbles: true}));
                    this.refresh();
                });
                this.normalModeGrid.append(
                    radio,
                    elt('label',
                        {
                            class: 'form-check-label',
                            for: id,
                        },
                        // if null or empty string, we display "Default View"
                        view.name ?? 'Default View'),
                );
            }


            // Then the edit mode
            const
                orderIcon = elt('i', {
                    class: 'bi-arrow-down-up fs-4',
                    style: 'color: var(--bs-gray-700);'}),
                viewNameInput =
                    elt('input', {
                            class: 'form-control',
                            value: view.name ?? 'Default View'}),
                hidden = view.hidden ? {checked: ''} : {},
                hideIcon = 
                    elt('graphic-toggle', { class: 'toggle-hide', ...hidden },
                        elt('i', {
                            class: 'fs-3 bi-eye',
                            style: 'color: var(--bs-gray-500);' }),
                        elt('i', {
                            class: 'fs-3 bi-eye-slash',
                            style: 'color: var(--bs-gray-700);' })),
                deleteIcon = 
                    elt('graphic-toggle', { class: 'toggle-delete' },
                        elt('i', {
                            class: 'fs-3 bi-trash',
                            style: 'color: var(--bs-gray-500);' }),
                        elt('i', {
                            class: 'fs-3 bi-trash',
                            style: 'color: var(--bs-red);' }),
                        );

            this.editModeGrid.append(
                orderIcon,
                viewNameInput,
                hideIcon,
                deleteIcon,
            );

        }

    }

    createView = () => {
        const visible = this.views.filter(view => !view.hidden);
        if(visible.length == 1 && this.current.name == null) {
            this.current.name = this.createViewNameInput.value;
            this.createViewNameInput.value = '';
            this.updateCurrentView();
            this.refresh();
        } else {
            const view: SavedViewConfig = clone(this.current);
            this.views.push(view);
            this.current = view;
            this.current.name = this.createViewNameInput.value;
            this.createViewNameInput.value = '';
            this.updateCurrentView();
            this.refresh();
        }
    }

    updateCurrentView() {
        this.dispatchEvent(
            new CustomEvent('update-current-view-config', {bubbles: true}));
    }

    updateList = () => {
        let i = 0;
        for(const input of this.editModeGrid.querySelectorAll('input')) {
            this.views[i].name = input.value;
            i++;
        }

        i = 0;
        for(const hide of this.editModeGrid.querySelectorAll('.toggle-hide')) {
            this.views[i].hidden = (hide as GraphicToggle).checked;
            i++;
        }

        // Make a list of indexes to delete, then iterate over them backwards
        // to do the deleting. TODO: this won't work after implementing
        // re-ordering of the views.
        i = 0;
        let indexes = [];
        for(const del of this.editModeGrid.querySelectorAll('.toggle-delete')) {
            if((del as GraphicToggle).checked) {
                indexes.push(i);
            }
            i++;
        }
        indexes.reverse();
        for(const i of indexes) {
            this.views.splice(i, 1);
        }

        // If they deleted all the views, push the default one back on the list
        if(this.views.length == 0) {
            this.current = clone(savedViewDefaults);
            this.views.push(this.current);
        }

        const visible = this.views.filter(view => !view.hidden);
        if(visible.length == 0) {
            this.current = clone(savedViewDefaults);
            this.views.push(this.current);
            visible.push(this.current);
        }

        // If they deleted the selected view, default to the first non-hidden.
        // By now we're guaranteed to at least have the default view.
        if(!visible.includes(this.current)) {
            this.current = this.views.find(view => !view.hidden);
        }
    };

}
export default WalkInViewsPanel;
window.customElements.define('walk-in-views-panel', WalkInViewsPanel);


const STYLESHEET = `
walk-in-views-panel {
    text-align: right;

    & .hidden {
        display: none;
    }

    & .normal-mode-grid {
        margin-bottom: 1rem;
        text-align: left;

        display: grid;
        align-items: center;
        grid-gap: 1rem;
        grid-template-columns: auto 1fr;

        & input[type=radio] {
            margin-top: 0;
        }

    }

    & .create-view-group {
        margin-bottom: 1rem;
    }

    & .edit-mode-grid {
        margin-bottom: 1rem;
        text-align: left;

        display: grid;
        align-items: center;
        grid-gap: 1rem;
        grid-template-columns: auto 1fr auto auto;
    }
}
`;
